import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  User, Bell, ShieldCheck, KeyRound, Smartphone, HelpCircle, FileText,
  Trash2, LogOut, ChevronRight, ArrowLeft, Mail,
} from 'lucide-react';
import ChangePasswordCard from '../components/Profile/ChangePasswordCard';
import LegalAndPrivacyCard from '../components/Profile/LegalAndPrivacyCard';
import AccountActions from '../components/Profile/AccountActions';
import NotificationSettings from '../components/Settings/NotificationSettings';

// Phase 6 UX (Feb 2026): unified Settings page for both roles.
//
// Consolidates account, notifications, privacy, security, install
// guidance, help/support, legal documents, delete account and log-out
// into a single scroll-able page. Sections deep-link from the top so a
// user landing here from a navigation menu can jump straight to what
// they came for.  We deliberately AVOID duplicating settings across
// multiple pages — the Profile page now links here for anything beyond
// "who am I" / "what did I do".

const SECTIONS = [
  { id: 'account', label: 'Account details', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'privacy', label: 'Privacy and data', icon: ShieldCheck },
  { id: 'security', label: 'Security and password', icon: KeyRound },
  { id: 'install', label: 'Installed app guidance', icon: Smartphone },
  { id: 'help', label: 'Help and support', icon: HelpCircle },
  { id: 'legal', label: 'Legal documents', icon: FileText },
  { id: 'danger', label: 'Delete account or log out', icon: Trash2 },
];

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const isManager = user?.role === 'manager';
  const homeHref = isManager ? '/manager' : '/worker';
  const profileHref = isManager ? '/manager/profile' : '/worker/profile';

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6 pb-24" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-full h-11 w-11 min-w-[44px] shrink-0" onClick={() => navigate(homeHref)} data-testid="settings-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">Settings</h1>
          <p className="text-sm text-slate-500">Manage your account, notifications and privacy.</p>
        </div>
      </div>

      {/* Quick jump index (desktop-friendly, still readable on mobile) */}
      <Card className="border-0 shadow-sm" data-testid="settings-index">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => jumpTo(id)}
                className="text-left rounded-lg p-2.5 hover:bg-slate-50 min-h-[48px] flex items-center gap-2 text-sm text-slate-700"
                data-testid={`settings-jump-${id}`}
              >
                <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Account details */}
      <section id="account" className="scroll-mt-4">
        <SectionHeading icon={User} title="Account details" sub="Your name, contact info and role. Edit sensitive fields from your Profile." />
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <Row label="Name" value={user?.fullName} />
            <Row label="Email" value={user?.email} locked hint="Contact support to change your email." />
            <Row label="Phone" value={user?.phone} />
            <Row label="Role" value={isManager ? 'Manager' : 'Worker'} locked />
            {isManager ? (
              <>
                <Row label="Default branch" value={user?.defaultBranchName} />
                <Row label="Postcode" value={user?.postcode} />
              </>
            ) : (
              <>
                <Row label="Home postcode" value={user?.postcode} />
                <Row label="Relevant experience" value={user?.experienceType} />
                <Row label="Smart ID" value={user?.smartIdVerified ? 'Verified' : (user?.smartIdSelfDeclared ? 'Self-declared: Yes' : 'Not declared')} locked hint="Confirmed with your manager on shift." />
              </>
            )}
            <Row label="Account status" value={user?.accountStatus === 'active' ? 'Active' : (user?.accountStatus || 'Unknown')} locked />
            <div className="pt-2">
              <Button variant="outline" className="rounded-full min-h-[44px]" onClick={() => navigate(profileHref)} data-testid="edit-profile-cta">
                Edit profile <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Notifications */}
      <section id="notifications" className="scroll-mt-4">
        <SectionHeading icon={Bell} title="Notifications" sub="Choose what we send you. Security and essential notices remain always on." />
        <NotificationSettings />
      </section>

      {/* Privacy & data */}
      <section id="privacy" className="scroll-mt-4">
        <SectionHeading icon={ShieldCheck} title="Privacy and data" sub="What we hold on you, how we use it, and your rights." />
        <LegalAndPrivacyCard />
      </section>

      {/* Security & password */}
      <section id="security" className="scroll-mt-4">
        <SectionHeading icon={KeyRound} title="Security and password" sub="Change your password. Two-step sign-in is coming soon." />
        <ChangePasswordCard />
      </section>

      {/* Installed app guidance */}
      <section id="install" className="scroll-mt-4">
        <SectionHeading icon={Smartphone} title="Installed app guidance" sub="Get ShiftCover365 on your phone for faster access." />
        <Card className="border-0 shadow-sm" data-testid="install-guidance-card">
          <CardContent className="p-5 space-y-3 text-sm text-slate-600">
            <p><strong className="text-slate-900">iPhone:</strong> Open ShiftCover365 in Safari, tap the Share icon, then <em>Add to Home Screen</em>.</p>
            <p><strong className="text-slate-900">Android:</strong> Open ShiftCover365 in Chrome, tap the menu, then <em>Add to Home Screen</em>.</p>
            <p className="text-xs text-slate-400">Already added it? Great — you&apos;ll see the ShiftCover365 icon on your home screen.</p>
          </CardContent>
        </Card>
      </section>

      {/* Help & support */}
      <section id="help" className="scroll-mt-4">
        <SectionHeading icon={HelpCircle} title="Help and support" sub="We're here during the pilot." />
        <Card className="border-0 shadow-sm" data-testid="help-support-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-slate-900">Contact support</p>
                <p className="text-sm text-slate-500 mt-0.5">Email us and we&apos;ll get back to you.</p>
                <a href="mailto:support@shiftcover365.co.uk" className="text-sm font-semibold text-[#4338CA] hover:underline inline-block mt-1.5" data-testid="support-email-link">
                  support@shiftcover365.co.uk
                </a>
                <p className="text-xs text-slate-500 mt-2" data-testid="support-response-note">
                  Response usually within <strong>3–5 business days</strong> during the pilot.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Legal documents */}
      <section id="legal" className="scroll-mt-4">
        <SectionHeading icon={FileText} title="Legal documents" sub="The full text of everything you've accepted." />
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col divide-y divide-slate-100">
              <LegalLink href="/privacy" label="Privacy Policy" description="How we collect and protect your personal data." testId="legal-link-privacy" />
              <LegalLink href="/terms" label="Terms of Use" description="The rules for using ShiftCover365." testId="legal-link-terms" />
              <LegalLink href="/cookies" label="Cookie Policy" description="How we use cookies and similar storage." testId="legal-link-cookies" />
              <LegalLink href="/accountability-agreement" label="Accountability Agreement" description="Your responsibilities around till and shift records." testId="legal-link-accountability" />
              <LegalLink href="/help" label="Help & Legal Overview" description="Support and legal guidance in one place." testId="legal-link-help" />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Danger zone */}
      <section id="danger" className="scroll-mt-4">
        <SectionHeading icon={Trash2} title="Delete account or log out" />
        <AccountActions />
        <Card className="border-0 shadow-sm mt-4">
          <CardContent className="p-5 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-base font-semibold text-slate-900">Log out of ShiftCover365</p>
              <p className="text-sm text-slate-500">You can sign back in any time.</p>
            </div>
            <Button
              variant="outline"
              className="rounded-full min-h-[48px] text-slate-800"
              onClick={() => setConfirmLogout(true)}
              data-testid="settings-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Log out
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Simple inline logout confirmation (no dialog dep needed) */}
      {confirmLogout && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="settings-logout-confirm">
          <Card className="w-full max-w-sm border-0 shadow-xl">
            <CardContent className="p-6">
              <p className="text-lg font-semibold text-slate-900 mb-1">Log out now?</p>
              <p className="text-sm text-slate-500 mb-5">You&apos;ll need your email and password to sign back in.</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full min-h-[48px]" onClick={() => setConfirmLogout(false)} data-testid="settings-logout-cancel">Stay signed in</Button>
                <Button className="flex-1 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white min-h-[48px]" onClick={() => { logout(); navigate('/login'); }} data-testid="settings-logout-confirm-btn">Log out</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function SectionHeading({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Row({ label, value, locked, hint }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2" data-testid={`row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide font-medium text-slate-400">{label}{locked && <span className="ml-1.5 inline-flex items-center text-[10px] text-amber-700 normal-case tracking-normal">· locked</span>}</p>
        <p className="text-base font-medium text-slate-900 mt-0.5 break-words">{value || 'Not set'}</p>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

function LegalLink({ href, label, description, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-3 py-3 min-h-[56px] hover:bg-slate-50 rounded-lg px-2 -mx-2"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-base font-medium text-slate-900">{label}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
    </a>
  );
}
